import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import * as moment from 'moment';
import SessionProp from './propTypes/sessionProp';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import {
  calculatePromoPercentage,
  promotionEndingMessage,
  SESSION_STATUS,
} from '../../services/utilityFunctions';
import UserProp from '../../commonComponents/propTypes/userProp';
import i18n from '../../services/i18n';
import PriceDisplay from '../../commonComponents/PriceDisplay';
import AcademyFooterContainer from '../../commonComponents/container/AcademyFooterContainer';
import LanguagesIcon from '../../commonComponents/icons/LanguagesIcon';
import MarkerIcon from '../../commonComponents/icons/MarkerIcon';
import UserBannerCard from '../../commonComponents/users/UserBannerCard';

export default function SessionItem({
  t,
  session,
  languages,
  mySessionsMode,
  isHostTrainerVisible,
  loggedUser,
  showPromotion,
  isPublicPage,
  showTeaserVideo,
}) {
  const {
    name,
    slug,
    image,
    language,
    summary,
    type,
    host,
    trainer,
    userPrice,
    userPromotionsPrice,
    promotionPriceDateEnd,
    owner,
    minUsers,
    maxUsers,
    adminStatus,
    startDate: sessionStartDate,
    endDate: sessionEndDate,
    participants,
  } = session;
  const imageURL =
    image && image.thumb && image.thumb !== ''
      ? image.thumb
      : '/images/no_image_upload.png';

  const shouldEdit =
    loggedUser && loggedUser._id === owner && type !== 'online';

  // Start and End Date
  let duration = null;
  if (sessionStartDate && sessionEndDate) {
    const startDate = moment(sessionStartDate).format('DD MMM, YYYY HH:mm');
    const endDate = moment(sessionEndDate).format('DD MMM, YYYY HH:mm');
    duration = `${startDate} - ${endDate}`;
  }
  const minMaxParticipants =
    !minUsers && !maxUsers ? '∞' : `${minUsers || 0} - ${maxUsers || '∞'}`;

  const hasWarning =
    adminStatus === SESSION_STATUS.PLANNED &&
    moment(sessionStartDate).diff(moment(), 'days') < 10 &&
    participants &&
    (participants.length || 0) < (minUsers || 0);

  // Footer - Language
  const languageValue = useMemo(() => {
    let languageStr = null;
    if (languages && languages.length > 0) {
      const lang = languages.find(l => l.code === language) || {};
      languageStr = lang.native || t('all');
    }
    return languageStr;
  }, [language, languages]);

  const containerClass = cx(
    'm-portlet',
    'm-portlet--rounded',
    'm-portlet--rounded-force',
    'academy-session-item',
  );
  const promotionMessage = promotionEndingMessage(promotionPriceDateEnd);
  const promoPercentage = calculatePromoPercentage(
    userPrice?.total,
    userPromotionsPrice?.total,
  );
  const promoPercentageMessage =
    promoPercentage &&
    i18n.__('product.promotion.percentageOff', promoPercentage);
  return (
    <div>
      {showPromotion && promotionMessage?.length > 0 && (
        <div className="flag-academy flag-academy-promotion">{`${promoPercentageMessage} ${promotionMessage}`}</div>
      )}
      <div className={containerClass}>
        <div className="m-portlet__body session-container-body session-container-content">
          <div className="row">
            {/** IMAGE */}
            <div className="col-lg-2 col-md-3">
              <div
                className={cx('session-image-wrapper', {
                  'teaser-card':
                    showTeaserVideo && session?.teaserVideo?.url,
                })}
              >
                <img className="session-image" src={imageURL} alt={name} />
                {showTeaserVideo && session?.teaserVideo?.url && (
                  <span
                    className="teaser-play-btn"
                    role="button"
                    aria-label={t('watchTrailer')}
                    data-trailer-url={session.teaserVideo.url}
                  >
                    <i className="fa fa-play" />
                  </span>
                )}
              </div>
            </div>
            <div className="col-lg-10 col-12">
              <div className="row">
                {/** NAME & SUMMARY */}
                <div className="col-lg-9 col-md-6">
                  <div className="session-details">
                    <div className="session-header">
                      <span className="session-header-name">{name}</span>
                    </div>
                    <div className="session-description-and-progress">
                      <div className="session-description">{summary}</div>
                    </div>
                  </div>
                </div>
                {/** PRICE */}
                <div
                  className="col-lg-3 col-12 px-0"
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <PriceDisplay
                    type="session"
                    t={t}
                    userPrice={userPrice}
                    userPromotionPrice={userPromotionsPrice}
                    withWrapper
                  />
                </div>
                <div className="col-lg-12 col-12 host-trainer-container">
                  {isHostTrainerVisible && host && (
                    <UserBannerCard user={host} label={t('sessions_host')} />
                  )}
                  {isHostTrainerVisible && trainer && (
                    <UserBannerCard
                      user={trainer}
                      label={t('sessions_trainer')}
                    />
                  )}
                  {mySessionsMode && (
                    <>
                      <div className="participants-my-session">
                        <div className="label">{`${t(
                          'sessions_participants',
                        )}:`}</div>
                        <div className="name">
                          {participants?.length || '0'}
                        </div>
                        {hasWarning && (
                          <i className="fas fa-exclamation-triangle text-danger" />
                        )}
                      </div>
                      <div className="participants-my-session">
                        <div className="label">{`${t(
                          'sessions_min_max_participants',
                        )}:`}</div>
                        <div className="name">{minMaxParticipants}</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="m-portlet__foot session-container-footer row">
          <div className="footer-info-container">
            {type && (
              <AcademyFooterContainer
                customIcon={<MarkerIcon />}
                text={t(`sessions_${type}`)}
              />
            )}
            {duration?.length > 0 && (
              <AcademyFooterContainer
                faIcon="la la-clock-o"
                text={duration || ''}
              />
            )}
            {languageValue && languageValue?.length > 0 && (
              <AcademyFooterContainer
                customIcon={<LanguagesIcon />}
                text={languageValue || ''}
              />
            )}
          </div>
          {!isPublicPage && (
            <div className="col-lg-2 col-12 offset-lg-1 session-itemList-btns">
              <div className="session-actions row">
                <a
                  href={`/academy/sessions/${slug}${
                    mySessionsMode ? '?mySessions=true' : ''
                  }`}
                  className="btn btn-sm m-btn--pill btn-dynamic navigation-button col-12"
                >
                  {t('sessions_openSession')}
                </a>
                {mySessionsMode && shouldEdit && (
                  <a href={`/academy/session/edit/${slug}`} className="col-12">
                    {adminStatus === SESSION_STATUS.CLOSED ||
                    adminStatus === SESSION_STATUS.CANCELED
                      ? t('sessions_view')
                      : t('sessions_edit')}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

SessionItem.propTypes = {
  t: PropTypes.func.isRequired,
  session: PropTypes.shape(SessionProp.propType).isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  mySessionsMode: PropTypes.bool,
  isHostTrainerVisible: PropTypes.bool,
  loggedUser: PropTypes.shape(UserProp.propType),
  showPromotion: PropTypes.bool,
  isPublicPage: PropTypes.bool,
  showTeaserVideo: PropTypes.bool,
};

SessionItem.defaultProps = {
  mySessionsMode: false,
  isHostTrainerVisible: true,
  loggedUser: null,
  showPromotion: false,
  isPublicPage: false,
  showTeaserVideo: false,
};
